import { Injectable, computed, signal } from "@angular/core";
import type { BoatImpactEvent } from "../../../simulation-core";

/**
 * Per-kind weight applied to the squared closing speed when converting
 * a collision into penalty points. Tuned so a brisk 1 m/s nudge into a
 * dock costs ~2 points, a serious 4 m/s ram costs ~32 points, and
 * grinding the hull on the bottom is slightly cheaper than slamming a
 * structure (the keel forgives a soft sand bank more than concrete).
 */
const KIND_WEIGHT: Record<BoatImpactEvent["kind"], number> = {
  dock: 2.0,
  boat: 2.5,
  ground: 1.5,
  buoy: 1.0,
  embankment: 2.2,
  powerline: 3.0,
  bridge: 3.0,
  obstacle: 2.5,
};

/** Aggregated stats about a player-boat impact. */
export interface ImpactRecord {
  kind: BoatImpactEvent["kind"];
  /** Closing speed at the contact, m/s. */
  speed: number;
  /** Penalty points charged for this single event. */
  points: number;
  /** `performance.now()` timestamp when the event was ingested. */
  tMs: number;
}

/**
 * Running tally of how roughly the player has been handling their boat.
 * The store is intentionally a pure accumulator — no UI logic — so the
 * scoring rules can change without touching any widgets, and so a
 * future end-of-trip "scorecard" screen can read the same numbers.
 *
 * Points use a quadratic in closing speed (`speed²`) so a hit that's
 * twice as fast costs four times as much. Both audio and a future HUD
 * already get the underlying events through {@link BoatImpactEvent}.
 */
@Injectable({ providedIn: "root" })
export class ImpactScoreStore {
  /** Total penalty points accumulated this session. Monotonic until reset. */
  readonly totalPoints = signal(0);
  /** Number of impact events charged so far. */
  readonly hitCount = signal(0);
  /** Peak closing speed observed across all impacts (m/s). */
  readonly worstSpeed = signal(0);
  /** Most recent impact (for any future "you just bumped X" indicator). */
  readonly lastImpact = signal<ImpactRecord | null>(null);

  /** Per-kind breakdown: number of hits + points subtotal. */
  readonly byKind = signal<
    Record<BoatImpactEvent["kind"], { count: number; points: number }>
  >({
    dock: { count: 0, points: 0 },
    boat: { count: 0, points: 0 },
    ground: { count: 0, points: 0 },
    buoy: { count: 0, points: 0 },
    embankment: { count: 0, points: 0 },
    powerline: { count: 0, points: 0 },
    bridge: { count: 0, points: 0 },
    obstacle: { count: 0, points: 0 },
  });

  /** Integer points display, rounded for convenience. */
  readonly totalPointsRounded = computed(() => Math.round(this.totalPoints()));

  /**
   * Accumulate a batch of events drained from the simulation in one
   * frame. Safe to call with an empty array.
   */
  ingest(events: readonly BoatImpactEvent[]): void {
    if (events.length === 0) return;
    const now = performance.now();
    let added = 0;
    let count = 0;
    let worst = this.worstSpeed();
    const byKind = { ...this.byKind() };
    let last: ImpactRecord | null = this.lastImpact();
    for (const ev of events) {
      const w = KIND_WEIGHT[ev.kind] ?? 1;
      const points = ev.speed * ev.speed * w;
      added += points;
      count += 1;
      if (ev.speed > worst) worst = ev.speed;
      const bucket = byKind[ev.kind];
      byKind[ev.kind] = {
        count: bucket.count + 1,
        points: bucket.points + points,
      };
      last = { kind: ev.kind, speed: ev.speed, points, tMs: now };
    }
    this.totalPoints.update((v) => v + added);
    this.hitCount.update((n) => n + count);
    this.worstSpeed.set(worst);
    this.byKind.set(byKind);
    this.lastImpact.set(last);
  }

  /** Zero the tally. Call when starting a new exercise / trip. */
  reset(): void {
    this.totalPoints.set(0);
    this.hitCount.set(0);
    this.worstSpeed.set(0);
    this.lastImpact.set(null);
    this.byKind.set({
      dock: { count: 0, points: 0 },
      boat: { count: 0, points: 0 },
      ground: { count: 0, points: 0 },
      buoy: { count: 0, points: 0 },
      embankment: { count: 0, points: 0 },
      powerline: { count: 0, points: 0 },
      bridge: { count: 0, points: 0 },
      obstacle: { count: 0, points: 0 },
    });
  }

  /**
   * Frozen snapshot of the running tally. Use {@link diffSince} to compute
   * what happened during a window (e.g. a Practice maneuver) without
   * resetting the global session counter.
   */
  snapshot(): ImpactScoreSnapshot {
    return {
      totalPoints: this.totalPoints(),
      hitCount: this.hitCount(),
    };
  }

  /**
   * Difference between the current tally and a previously captured
   * {@link snapshot}. Returns only the additive fields — for per-window
   * "worst contact speed" the caller should watch {@link lastImpact}
   * directly (the aggregate `worstSpeed` is monotonic and cannot be
   * subtracted).
   */
  diffSince(prev: ImpactScoreSnapshot): ImpactScoreDiff {
    return {
      addedPoints: Math.max(0, this.totalPoints() - prev.totalPoints),
      addedHits: Math.max(0, this.hitCount() - prev.hitCount),
    };
  }
}

export interface ImpactScoreSnapshot {
  readonly totalPoints: number;
  readonly hitCount: number;
}

export interface ImpactScoreDiff {
  readonly addedPoints: number;
  readonly addedHits: number;
}
